import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import titleize from '@vangst/lib/titleize'
import { prefetchCompanyJobPostings } from '@vangst/services/oogst/company/useCompanyJobPostings'
import { fetchPage } from '@vangst/services/oogst/page'
import {
  fetchTraining,
  useTraining,
} from '@vangst/services/oogst/training/useTraining'
import {
  QuizFragment,
  RobotsEnum,
  TrainingFragment,
} from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { MdOutlineAddTask, MdSchedule } from 'react-icons/md'
import MastIcon from '../../../../../../../components/assets/MastIcon'
import Busy from '../../../../../../../components/feedback/Busy'
import Breadcrumbs from '../../../../../../../components/navigation/Breadcrumbs'
import Markdown from '../../../../../../../components/views/Markdown'
import Meta from '../../../../../../../domains/application/Meta'
import CompanyJobPostingsRenderer from '../../../../../../../domains/company/CompanyJobPostingsRenderer'
import CourseDetail from '../../../../../../../domains/course/CourseDetail'
import LearnPlayer from '../../../../../../../domains/course/LearnPlayer'
import CourseTabs from '../../../../../../../domains/course/navigation/CourseTabs'
// @ts-expect-error - no types file
import grabLink from 'youtube-thumbnail-grabber'
import ClickyLink from '../../../../../../../components/navigation/ClickyLink'

type StaticProps = GetStaticPropsContext<{
  displayname?: string
  id?: string
  tid?: string
}>

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const id = params?.id
  const tid = params?.tid
  const displayname = params?.displayname

  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[displayname]', displayname || '')
    .replace('[tid]', tid || '')
    .replace('[id]', id || '')

  const jobVars = displayname ? { displayname, first: 4 } : null
  if (jobVars) await prefetchCompanyJobPostings(queryClient, jobVars)

  const training = tid
    ? (await fetchTraining(queryClient, { id: tid })).getTraining
    : null

  const trainingMembers = {
    label: `Course taken ${
      training?.countCandidatesCompletedTraining ?? 0
    } time${training?.countCandidatesCompletedTraining === 1 ? '' : 's'}`,
    uri: training
      ? Routes.COMPANIES_TRAINING_MEMBERS.replace(
          ':displayname',
          training.creatorClient.displayname ?? '',
        ).replace(':tid', tid ?? '')
      : null,
  }

  const quizzes = training?.quizzes || ([] as QuizFragment[])
  const quiz = quizzes.find((q) => q.slug === id)

  const structuredCourseData = {
    '@context': 'https://schema.org/',
    '@type': 'Course',
    name: quiz?.title,
    description: quiz?.description,
    provider: {
      '@type': 'Organization',
      name: 'Vangst',
      sameAs: 'https://www.vangst.com',
    },
  }
  const structuredVideoData = {
    '@context': 'https://schema.org/',
    '@type': ['VideoObject', 'LearningResource'],
    name: quiz?.title ?? 'Cannabis Quiz',
    description: quiz?.description ?? null,
    learningResourceType: 'Concept Overview',
    educationLevel: 'High school (US)',
    contentUrl: quiz?.videoUrl ?? null,
    thumbnailUrl: [grabLink(quiz?.videoUrl, 'max')],
    uploadDate: quiz?.createdAt,
  }

  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = { ...getPage, structuredCourseData, structuredVideoData }

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.COMPANIES, text: 'Companies' },
    {
      href: Routes.COMPANIES_DETAIL.replace(
        ':displayname',
        displayname ?? '',
      ) as Routes,
      text: titleize(displayname || 'Company'),
    },
    {
      href: Routes.COMPANIES_TRAININGS.replace(
        ':displayname',
        displayname ?? '',
      ) as Routes,
      text: 'Trainings',
    },
    { href: null, text: titleize(training?.title || 'Course') },
  ]

  return {
    props: {
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      crumbs,
      id,
      tid,
      jobVars,
      page,
      trainingMembers,
      quizzes,
      quiz,
    },
    notFound: training == null,
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function CompanyQuizPage(props: PropsType) {
  const { crumbs, id, jobVars, page, tid, trainingMembers, quizzes, quiz } =
    props
  const { data: training, isLoading: trainingIsLoading } = useTraining(
    { id: tid || '' },
    { enabled: tid != null },
  )
  if (!page || trainingIsLoading || !training || !quiz)
    return (
      <article className="contain-xyc mood-white">
        <Busy />
      </article>
    )

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description ?? training.description}
        image={page.pagePhotos?.[0]?.url}
        robots={
          page.metaInfo?.robots ??
          (training.isPublished === false
            ? RobotsEnum.NoindexNofollow
            : RobotsEnum.IndexFollow)
        }
        title={page.metaInfo?.title ?? page.title}
      >
        {page.structuredCourseData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(page.structuredCourseData),
            }}
          />
        )}
        {page.structuredVideoData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(page.structuredVideoData),
            }}
          />
        )}
      </Meta>
      <Breadcrumbs crumbs={crumbs} />
      <header className="mood-white">
        <div className="contain-xyc relative flex flex-col justify-center gap-4 md:min-h-[26rem]">
          <div className="contain-y z-10 flex flex-col gap-2 md:flex-row md:gap-8">
            <div className="flex max-w-5xl flex-col gap-4">
              <h1 className="break-words heading-xl">
                {page.title ?? training.title ?? 'Course'}
              </h1>
              <ul className="ml-2 flex gap-4">
                <li className="flex gap-2">
                  <MdSchedule />
                  <span>{training.totalTimeToComplete} Minutes</span>
                </li>
                <li className="flex gap-2 border-l pl-4">
                  <MdOutlineAddTask />
                  <span>{training.totalQuestionsCount} Questions</span>
                </li>
                {training.permissions.viewTrainingMembers &&
                  trainingMembers.uri && (
                    <li className="flex gap-2 border-l pl-4">
                      <ClickyLink
                        href={trainingMembers.uri}
                        className="link-grey underline"
                      >
                        {trainingMembers.label}
                      </ClickyLink>
                    </li>
                  )}
              </ul>
              <div className="ml-2 mt-4 break-words text-sm sm:text-md">
                <Markdown renderType="basic">
                  {page.description ?? training.description ?? ''}
                </Markdown>
              </div>
            </div>
            <div
              className="order-first flex shrink-0 text-[4rem] text-green sm:text-[6rem]"
              role="presentation"
            >
              <MastIcon as="school" size="1em" />
            </div>
          </div>
        </div>
      </header>

      <div className="contain-c template-content-aside mood-white">
        <article className="contain-x md:min-h-120">
          <LearnPlayer videoUrl={quiz.videoUrl} />
          <CourseTabs
            currentQuizId={id ?? ''}
            // TODO: stop having to cast these
            quizzes={quizzes as QuizFragment[]}
            training={training as TrainingFragment}
          />
        </article>

        <aside id="aside" className="contain-xc">
          <section className="mood-blue-dark flex h-full flex-col p-4 sm:p-8">
            <CourseDetail id={id} training={training as TrainingFragment} />
          </section>
        </aside>
      </div>

      <div className="contain-xyc mt-16">
        <h2 className="mood-orange flex h-16 items-center pl-6 font-600">
          Related Jobs
        </h2>
        <div className="mood-grey-lightest p-4">
          {jobVars ? (
            <CompanyJobPostingsRenderer
              as="card"
              at="preview"
              className="mt-4"
              classNameList="grid grid-four-up"
              variables={jobVars}
            />
          ) : (
            <span>Check back soon!</span>
          )}
        </div>
      </div>
    </>
  )
}
